import AbstractBaseApi from "@/apis/AbstractBaseApi";
import type {
  BandWidth,
  BandWidthEmpty,
  IfStatus,
  MaximumPrefix,
  Priority,
  RouteAggregation,
  VpnVnCode,
} from "@/apis/CloudVnMsApi";

export interface CloudVnGpu {
  /** E番号. */
  enumber: string;
  /**
   * アクセス品目.
   * 以下のいずれかを指定する。
   * MULTI_CLOUD_GW：マルチクラウドGW
   */
  accessType: "MULTI_CLOUD_GW";
  /** 帯域. GET時に登録された帯域が存在しない場合は「0」を指定する。 */
  bandwidth: BandWidth | BandWidthEmpty;
  /** 事業所名. */
  office: string | null;
  /**
   * クラウドVN回線(GPU)排他情報. 排他確認用パラメータ
   * 設定変更を行う場合にはGETメソッドにて通知した内容をそのまま渡すこと。
   */
  cloudVnGpuKeyId: string;
}

export interface CloudVnGpuCommon {
  /** GPU種別 */
  type: "BARE_METAL";
  /**
   * クラウドサービス利用拠点.
   * 西日本の一択
   */
  location: "WEST_JAPAN";
  /** VPN/VNコード */
  vpnVnCode: VpnVnCode;
  /** 仮想ネットワークタイプ. L3固定 */
  vnType: "L3";
  /** 仮想ネットワーク名. */
  vnName: string | null;
  /** ルーティング種別. BGP4固定 */
  routingType: "BGP4";
  /** 帯域タイプ. PF固定 */
  bandwidthType: "PF";
  /** 帯域 */
  bandwidth: BandWidth;
  /**
   * 経路集約.
   * OFF、ON、CUSTOMのいずれかを指定する。
   * OFF：経路集約を非表示
   * ON：経路集約を表示
   * CUSTOM：経路集約アドレスを入力
   */
  routeAggregation: RouteAggregation;
  /**
   * 経路集約アドレスリスト.
   * 経路集約が「CUSTOM」選択時は必須項目
   * 登録順でソートする。
   */
  routeAggregationAddressList: Array<string> | null;
  /** AS番号. */
  asNumber: 2516;
  /**
   * MD5文字列.
   * 「Kddicloud」固定
   */
  md5Key: "Kddicloud";
  /**
   * Maximum prefix(設定値上限取得APIのBGP4の最大経路数).
   * 設定値上限APIのGETから上限値を取得し、その上限値以下の値を設定可能とする。
   * 値は100,200,300,400,500,1000,1500,2000から指定する。
   */
  maximumPrefix: MaximumPrefix;
  /** M番号 */
  mNumber: string;
  /**
   * プロビステータス
   */
  provStatus:
    | "SUCCESS"
    | "CREATING"
    | "CREATE_FAILED"
    | "RETRYABLE"
    | "FAILED"
    | "PENDING_DELETE"
    | "DELETE_RETRYABLE";
  /**
   * KBIテナントコード
   */
  kbiTenantCode: string;
  /**
   * エラー詳細
   */
  errorDetail: string | null;
  /** 設定値の説明  */
  description: string | null;
}

export interface CloudVnGpuActSby {
  /** クラウドVNコネクトSEQ. */
  cloudVnConnectSeq: string;
  /** VNコネクト名. */
  vnConnectName: string;
  /**
   * W番号.
   * 主キー
   */
  wnumber: string;
  /**
   * IF状態. VNコネクト状態が無効の場合省略
   * NO_SHUTDOWN: 開放状態
   * SHUTDOWN: 閉塞状態
   */
  ifStatus: IfStatus;
  /** VNコネクト有効/無効フラグ. true:有効 false:無効 */
  isVnConnectStatus: boolean;
  /** ステータス */
  status: string;
  /** WANアドレス. */
  wanAddress: string;
  /** ピアIPアドレス */
  peerIpAddress: string;
  /**
   * ローカルプリファレンス.
   * HIGH_PRIORITY:優先
   * LOW_PRIORITY:非優先
   * ACT回線識別フラグがtrueの場合:「HIGH_PRIORITY」固定
   * ACT回線識別フラグがfalseの場合:「LOW_PRIORITY」固定
   */
  localPreference: Priority;
  /**
   * MED.
   * HIGH_PRIORITY:優先
   * LOW_PRIORITY:非優先
   * ACT回線識別フラグがtrueの場合:「HIGH_PRIORITY」固定
   * ACT回線識別フラグがfalseの場合:「LOW_PRIORITY」固定
   */
  med: Priority;
}

export type CloudVnGpuList = CloudVnGpu & {
  /**
   * 総件数（クラウドVN回線）.
   * 一覧表示対象の総件数
   */
  cloudVnConnectTotal: number;
  cloudVnConnectsList: {
    cloudVnConnectCommon: Pick<
      CloudVnGpuCommon,
      | "type"
      | "location"
      | "vpnVnCode"
      | "vnType"
      | "routingType"
      | "bandwidthType"
      | "bandwidth"
      | "provStatus"
      | "mNumber"
      | "description"
    >;
    cloudVnConnectAct: Pick<
      CloudVnGpuActSby,
      | "cloudVnConnectSeq"
      | "vnConnectName"
      | "wnumber"
      | "ifStatus"
      | "isVnConnectStatus"
      | "status"
    >;
    cloudVnConnectSby: CloudVnGpuList["cloudVnConnectsList"][0]["cloudVnConnectAct"];
  }[];
};

export type CloudVnGpuDetail = CloudVnGpu & {
  cloudVnConnectCommon: CloudVnGpuCommon;
  cloudVnConnectAct: CloudVnGpuActSby;
  cloudVnConnectSby: CloudVnGpuActSby;
};

/**
 * 設定追加には以下の3パターンがある。
 *
 * ACT/SBY回線を同時に追加するパターン
 * また以下の項目は固定値となり、API内部で設定するため省略する。
 * ・MED
 *   ACT回線の場合は「HIGH_PRIORITY」固定
 *   SBY回線の場合は「LOW_PRIORITY」固定
 * ・ローカルプレファレンス
 *   ACT回線の場合は「HIGH_PRIORITY」固定
 *   SBY回線の場合は「LOW_PRIORITY」固定
 * ・ルーティング種別
 *   BGP4固定
 * ・md5文字列
 *  「kddicloud」固定
 */
export interface CloudVnGpuPost {
  /** ACT/SBY回線を同時に追加するパターン、ACT回線のみを追加するパターンでは必須となる。 */
  cloudVnConnectCommon: Pick<
    CloudVnGpuCommon,
    | "type"
    | "location"
    | "vpnVnCode"
    | "bandwidthType"
    | "bandwidth"
    | "routeAggregation"
    | "routeAggregationAddressList"
    | "asNumber"
    | "maximumPrefix"
    | "mNumber"
    | "kbiTenantCode"
    | "description"
  > | null;
  /** ACT/SBY回線を同時に追加するパターン、ACT回線のみを追加するパターンでは必須となる。 */
  cloudVnConnectAct: Pick<
    CloudVnGpuActSby,
    "vnConnectName" | "wanAddress" | "peerIpAddress"
  > | null;
  /** ACT/SBY回線を同時に追加するパターン、SBY回線のみを追加するパターンでは必須となる。 */
  cloudVnConnectSby: CloudVnGpuPost["cloudVnConnectAct"];
  /** 操作履歴の備考 */
  note?: string;
  /**
   * クラウドVN回線(GPU)排他情報. 排他確認用パラメータ
   * 設定変更を行う場合にはGETメソッドにて通知した内容をそのまま渡すこと。
   */
  cloudVnGpuKeyId: string;
}

export interface CloudVnGpuPut {
  cloudVnConnectCommon: Pick<
    CloudVnGpuCommon,
    | "bandwidth"
    | "routeAggregation"
    | "routeAggregationAddressList"
    | "maximumPrefix"
    | "description"
  >;
  cloudVnConnectAct: Pick<CloudVnGpuActSby, "vnConnectName">;
  cloudVnConnectSby: Pick<CloudVnGpuActSby, "vnConnectName">;
  /** 操作履歴の備考 */
  note?: string;
  /**
   * クラウドVN回線(GPU)排他情報. 排他確認用パラメータ
   * 設定変更を行う場合にはGETメソッドにて通知した内容をそのまま渡すこと。
   */
  cloudVnGpuKeyId: string;
}

export interface CloudVnGpuOrder {
  /** W番号. 主キー */
  cloudVnConnectList: string[];
  /**
   * クラウドVN回線(GPU)排他情報. 排他確認用パラメータ
   * 設定変更を行う場合にはGETメソッドにて通知した内容をそのまま渡すこと。
   */
  cloudVnGpuKeyId: string;
}

export type CloudVnGpuDelete = CloudVnGpuOrder & {
  /** 操作履歴の備考 */
  note?: string;
};

export interface CloudVnGpuRetry {
  /**
   * 再実行可能ステータス
   */
  cloudVnGpuKeyId: string;
}

/** クラウドVN回線(GPU)API */
export default class CloudVnGpuApi extends AbstractBaseApi {
  /**
   * クラウドVN回線設定を取得
   * @param cloudLineSeq クラウド回線SEQ
   */
  async getVnConnects(cloudLineSeq: string): Promise<CloudVnGpuList> {
    return (
      await this.api.get(
        `/v1/network/cloud/line/${cloudLineSeq}/kddi-gpu-cloud/vnconnects`
      )
    ).data;
  }

  /**
   * クラウドVN回線設定を追加
   * @param cloudLineSeq クラウド回線SEQ
   * @param data クラウドVN回線設定
   */
  async postVnConnect(
    cloudLineSeq: string,
    data: CloudVnGpuPost
  ): Promise<void> {
    await this.api.post(
      `/v1/network/cloud/line/${cloudLineSeq}/kddi-gpu-cloud/vnconnects`,
      data
    );
  }

  /**
   * クラウドVN回線設定を変更
   * @param cloudLineSeq クラウド回線SEQ
   * @param wnumber W番号. ACT回線のW番号を指定する
   * @param data クラウドVN回線設定
   */
  async putVnConnect(
    cloudLineSeq: string,
    wnumber: string,
    data: CloudVnGpuPut
  ): Promise<void> {
    await this.api.post(
      `/v1/network/cloud/line/${cloudLineSeq}/kddi-gpu-cloud/vnconnects/${wnumber}`,
      data
    );
  }

  /**
   * クラウドVNコネクトの有効/無効状態を有効に変更
   * @param data 有効情報
   */
  async putEnableVnConnect(data: CloudVnGpuOrder): Promise<void> {
    await this.api.post(
      `/v1/network/cloud/line/kddi-gpu-cloud/vnconnects/enable`,
      data
    );
  }

  /**
   * クラウドVNコネクトの有効/無効状態を無効に変更
   * @param data 無効情報
   */
  async putDisableVnConnect(data: CloudVnGpuOrder): Promise<void> {
    await this.api.post(
      `/v1/network/cloud/line/kddi-gpu-cloud/vnconnects/disable`,
      data
    );
  }

  /**
   * クラウドVN回線設定の詳細情報を取得
   * @param wnumber W番号. ACT回線のW番号を指定する。
   */
  async getVnConnect(wnumber: string): Promise<CloudVnGpuDetail> {
    return (
      await this.api.get(
        `/v1/network/cloud/line/kddi-gpu-cloud/vnconnect/${wnumber}`
      )
    ).data;
  }

  /**
   * クラウドVN回線設定を削除
   * @param cloudLineSeq
   * @param data
   */
  async deleteVnConnect(
    cloudLineSeq: string,
    data: CloudVnGpuDelete
  ): Promise<void> {
    await this.api.post(
      `/v1/network/cloud/line/${cloudLineSeq}/kddi-gpu-cloud/vnconnects/delete`,
      data
    );
  }

  /**
   * クラウドVN回線設定を再実行
   */
  async cloudVnGpuRetry(
    cloudLineSeq: string,
    wnumber: string,
    data: CloudVnGpuRetry
  ): Promise<void> {
    await this.api.post(
      `/v1/network/cloud/line/${cloudLineSeq}/kddi-gpu-cloud/vnconnects/${wnumber}/retry`,
      data
    );
  }
}
